import { useCallback, useEffect, useRef, useState } from "react";
import type { FeedBeforeResponse, FeedRecentResponse, StoredEvent, WsMessage } from "../types";

export type ConnectionStatus = "connecting" | "connected" | "disconnected";

export interface UseFeedResult {
  events: StoredEvent[];
  loadingOlder: boolean;
  hasMoreHistoric: boolean;
  status: ConnectionStatus;
  loadOlder: () => Promise<void>;
}

function dedupeAndSort(events: StoredEvent[]): StoredEvent[] {
  const seen = new Set<string>();
  const out: StoredEvent[] = [];
  for (const e of events) {
    const sig = e.payload.signature;
    if (seen.has(sig)) continue;
    seen.add(sig);
    out.push(e);
  }
  out.sort((a, b) => a.receivedAt - b.receivedAt);
  return out;
}

export function useFeed(): UseFeedResult {
  const [events, setEvents] = useState<StoredEvent[]>([]);
  const [hasMoreHistoric, setHasMoreHistoric] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [status, setStatus] = useState<ConnectionStatus>("connecting");

  const loadingOlderRef = useRef(false);
  const seenSigsRef = useRef<Set<string>>(new Set());

  const ingest = useCallback((incoming: StoredEvent[]) => {
    if (incoming.length === 0) return;
    setEvents((prev) => {
      const merged = dedupeAndSort([...prev, ...incoming]);
      seenSigsRef.current = new Set(merged.map((e) => e.payload.signature));
      return merged;
    });
  }, []);

  // initial fetch
  useEffect(() => {
    let cancelled = false;
    fetch("/api/feed/recent")
      .then((r) => r.json() as Promise<FeedRecentResponse>)
      .then((data) => {
        if (cancelled) return;
        setHasMoreHistoric(data.hasMore);
        ingest(data.events);
      })
      .catch((err) => {
        console.error("initial feed load failed", err);
      });
    return () => {
      cancelled = true;
    };
  }, [ingest]);

  // websocket
  useEffect(() => {
    let stopped = false;
    let backoff = 1000;
    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let firstConnect = true;

    const connect = () => {
      setStatus("connecting");
      const proto = window.location.protocol === "https:" ? "wss" : "ws";
      ws = new WebSocket(`${proto}://${window.location.host}/ws`);

      ws.onopen = () => {
        setStatus("connected");
        backoff = 1000;
        if (!firstConnect) {
          fetch("/api/feed/recent")
            .then((r) => r.json() as Promise<FeedRecentResponse>)
            .then((data) => ingest(data.events))
            .catch(() => {});
        }
        firstConnect = false;
      };

      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data) as WsMessage;
          if (msg.type === "event") {
            ingest([msg.event]);
          }
        } catch {
          // ignore
        }
      };

      ws.onerror = () => {
        ws?.close();
      };

      ws.onclose = () => {
        if (stopped) return;
        setStatus("disconnected");
        reconnectTimer = setTimeout(connect, backoff);
        backoff = Math.min(backoff * 2, 15000);
      };
    };

    connect();
    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      ws?.close();
    };
  }, [ingest]);

  const loadOlder = useCallback(async () => {
    if (loadingOlderRef.current) return;
    if (events.length === 0) return;
    if (!hasMoreHistoric) return;
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    try {
      const oldest = events[0]!;
      const res = await fetch(`/api/feed/before?before=${oldest.receivedAt}&limit=100`);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = (await res.json()) as FeedBeforeResponse;
      setHasMoreHistoric(data.hasMore);
      if (data.events.length > 0) ingest(data.events);
    } catch (err) {
      console.error("load older failed", err);
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  }, [events, hasMoreHistoric, ingest]);

  return { events, loadingOlder, hasMoreHistoric, status, loadOlder };
}
